<template>
  <!--
    Root is a flex column that fills its parent both via `h-100` (works when
    parents have an explicit height set, e.g. the desktop sticky sidebar) and
    `flex-grow-1` (works inside flex-column parents like the mobile v-card
    inside the bottom sheet). Either way, the body grows and the footer
    stays anchored to the bottom.
  -->
  <div class="parts-filter-panel d-flex flex-column h-100 flex-grow-1">
    <div v-if="showHeader" class="parts-filter-panel__header d-flex align-center justify-space-between py-4">
      <span class="text-h6 font-weight-semibold">{{ t(strings.filters) }}</span>
      <v-btn
        v-if="closable"
        icon="mdi-close"
        variant="text"
        density="comfortable"
        :aria-label="t(strings.filterSheetCloseAria)"
        @click="emit('close')"
      />
    </div>

    <div class="parts-filter-panel__body flex-grow-1 px-0 pt-0 pb-2">
      <v-expansion-panels
        v-model="expansionPanels"
        multiple
        variant="accordion"
        flat
        elevation="0"
        class="parts-filter-panels"
      >
        <v-expansion-panel>
          <v-expansion-panel-title class="parts-filter-expansion-title py-3">
            <div class="parts-filter-expansion-title__text w-100 pe-2">
              <div class="text-caption text-high-emphasis text-uppercase">
                {{ t(strings.manufacturerLabel) }}
              </div>
              <div
                v-if="
                  manufacturerSelectionSummary && !isSectionOpen(0)
                "
                class="text-body-2 text-medium-emphasis mt-1 text-truncate"
              >
                {{ manufacturerSelectionSummary }}
              </div>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="px-0">
            <div
              v-if="manufacturerOptions.length === 0"
              class="text-body-2 text-medium-emphasis py-1"
            >
              —
            </div>
            <v-checkbox
              v-for="opt in manufacturerOptions"
              :key="String(opt.value)"
              :model-value="manufacturers"
              :value="opt.value"
              :label="opt.label"
              hide-details
              density="compact"
              color="primary"
              class="parts-filter-checkbox"
              @update:model-value="(v) => emit('update:manufacturers', toStringArray(v))"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-title class="parts-filter-expansion-title py-3">
            <div class="parts-filter-expansion-title__text w-100 pe-2">
              <div class="text-caption text-high-emphasis text-uppercase">
                {{ t(strings.conditionLabel) }}
              </div>
              <div
                v-if="conditionSelectionSummary && !isSectionOpen(1)"
                class="text-body-2 text-medium-emphasis mt-1 text-truncate"
              >
                {{ conditionSelectionSummary }}
              </div>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="px-0">
            <div
              v-if="conditionOptions.length === 0"
              class="text-body-2 text-medium-emphasis py-1"
            >
              —
            </div>
            <v-checkbox
              v-for="opt in conditionOptions"
              :key="opt.value"
              :model-value="conditions"
              :value="opt.value"
              :label="opt.label"
              hide-details
              density="compact"
              color="primary"
              class="parts-filter-checkbox"
              @update:model-value="(v) => emit('update:conditions', toStringArray(v))"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-title class="parts-filter-expansion-title py-3">
            <div class="parts-filter-expansion-title__text w-100 pe-2">
              <div class="text-caption text-high-emphasis text-uppercase">
                {{ t(strings.partTypeLabel) }}
              </div>
              <div
                v-if="partTypeSelectionSummary && !isSectionOpen(2)"
                class="text-body-2 text-medium-emphasis mt-1 text-truncate"
              >
                {{ partTypeSelectionSummary }}
              </div>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="px-0">
            <div
              v-if="partTypeOptions.length === 0"
              class="text-body-2 text-medium-emphasis py-1"
            >
              —
            </div>
            <v-checkbox
              v-for="opt in partTypeOptions"
              :key="opt.value"
              :model-value="partTypes"
              :value="opt.value"
              :label="opt.label"
              hide-details
              density="compact"
              color="primary"
              class="parts-filter-checkbox"
              @update:model-value="(v) => emit('update:partTypes', toStringArray(v))"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <div class="parts-filter-panel__footer flex-shrink-0 pt-2">
      <v-btn
        v-if="showDone"
        block
        color="primary"
        variant="flat"
        size="large"
        rounded="lg"
        class="text-none font-weight-semibold"
        @click="emit('done')"
      >
        {{ t(strings.filterDone) }}
      </v-btn>
      <v-btn
        block
        variant="text"
        color="primary"
        size="large"
        class="text-none font-weight-semibold"
        :class="showDone ? 'mt-2' : ''"
        :disabled="!hasActiveSelections"
        @click="emit('clear')"
      >
        {{ t(strings.clearFilters) }}
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { parts_table_strings as strings } from "./PartsTable.strings.ts";
import { useReverseT } from "../../i18n.ts";

export interface PartsFilterOption {
  value: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    /** Selected manufacturer values (v-model). */
    manufacturers: string[];
    /** Selected condition values (v-model). */
    conditions: string[];
    /** Selected part-type values (v-model). */
    partTypes: string[];
    /** Available options for each filter group. */
    manufacturerOptions: PartsFilterOption[];
    conditionOptions: PartsFilterOption[];
    partTypeOptions: PartsFilterOption[];
    /**
     * If `condition`-style values need pretty labels (e.g. "as removed" →
     * "As Removed"), pass a resolver. Used only for the collapsed-section
     * summary strings.
     */
    conditionDisplayName?: (value: string) => string;
    /**
     * Indices of panels that should be open initially.
     *
     *   - Mobile bottom sheet: pass `[]` so panels start collapsed (the
     *     existing behavior).
     *   - Desktop inline sidebar: pass `[0, 1, 2]` so all groups are
     *     visible without an extra click.
     */
    initialExpanded?: number[];
    /** Show the "Filters" title + close button header (used in the bottom sheet). */
    showHeader?: boolean;
    /** Whether the header's close button is present (only meaningful with showHeader). */
    closable?: boolean;
    /** Show the "Done" CTA (mobile bottom sheet only — desktop doesn't need it). */
    showDone?: boolean;
  }>(),
  {
    initialExpanded: () => [],
    showHeader: false,
    closable: false,
    showDone: false,
  },
);

const emit = defineEmits<{
  "update:manufacturers": [value: string[]];
  "update:conditions": [value: string[]];
  "update:partTypes": [value: string[]];
  done: [];
  clear: [];
  close: [];
}>();

const { t } = useReverseT();

/**
 * `v-checkbox` with `:model-value` + `:value` semantics in Vuetify 3:
 * when used as part of a checkbox group, the model-value receives the full
 * array. But Vue's type narrowing on a single-checkbox update event widens
 * the value to `boolean | string | string[]`. Normalize back to a clean
 * `string[]` before re-emitting upstream.
 */
function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === "string");
  }
  return [];
}

/* ----------------------- Section open/closed state ------------------ */

const expansionPanels = ref<number[]>([...props.initialExpanded]);

watch(
  () => props.initialExpanded,
  (next) => {
    expansionPanels.value = [...next];
  },
);

function isSectionOpen(index: number): boolean {
  return expansionPanels.value.includes(index);
}

/* ----------------------- Collapsed-state summaries ------------------ */

const manufacturerSelectionSummary = computed(() =>
  [...props.manufacturers]
    .sort((a, b) => a.localeCompare(b))
    .join(", "),
);

const conditionSelectionSummary = computed(() => {
  const display = props.conditionDisplayName ?? ((v: string) => v);
  return [...props.conditions]
    .sort((a, b) => display(a).localeCompare(display(b)))
    .map(display)
    .join(", ");
});

const partTypeSelectionSummary = computed(() =>
  [...props.partTypes].sort((a, b) => a.localeCompare(b)).join(", "),
);

const hasActiveSelections = computed(
  () =>
    props.manufacturers.length > 0 ||
    props.conditions.length > 0 ||
    props.partTypes.length > 0,
);
</script>

<style scoped>
.parts-filter-panel {
  background: rgb(var(--v-theme-surface));
}

.parts-filter-panel__body {
  min-height: 0;
  overflow-y: auto;
  padding-left: 16px;
  padding-right: 16px;
}

.parts-filter-panel__header {
  padding-left: 16px;
  padding-right: 16px;
  border-bottom: 1px solid
    rgba(var(--v-border-color), var(--v-border-opacity));
}

.parts-filter-panel__footer {
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid
    rgba(var(--v-border-color), var(--v-border-opacity));
}

/* flat + elevation 0: no card shadow; light dividers between sections */
.parts-filter-panels :deep(.v-expansion-panel) {
  border: none !important;
  border-radius: 0 !important;
  border-bottom: 1px solid
    rgba(var(--v-border-color), var(--v-border-opacity)) !important;
}

.parts-filter-panels :deep(.v-expansion-panel:last-child) {
  border-bottom: none !important;
}

.parts-filter-panels :deep(.v-expansion-panel--active:not(:first-child)),
.parts-filter-panels :deep(.v-expansion-panel--active + .v-expansion-panel) {
  margin-top: 0 !important;
}

.parts-filter-expansion-title :deep(.v-expansion-panel-title__append) {
  align-self: flex-start;
  padding-top: 2px;
}

.parts-filter-expansion-title__text {
  min-width: 0;
}

.parts-filter-checkbox {
  min-height: 40px;
}
</style>
